import { useNavigate, useSearchParams } from "react-router-dom";
import React, {
  RefObject,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  acceptOrDeclineInviteConfirmation,
  forwardInterview,
} from "@endpoints/interview.service";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import {
  IGetInterviewByIdResponse,
  ISpecificSlotResponse,
  User,
} from "@endpoints/types/interview.type";
import { EnumUserTypes, userTypes } from "@utils/constants";
import { RootState } from "@store/reducers";
import { useSelector } from "react-redux";
import {
  IAvailabilitySlot,
  MeetingParties,
  slotStatus,
  TypeGroupOfProposals,
} from "../MeetingPollDetailPage";
import dayjs from "@lib/dayjs";
import PlusCircleIcon from "@assets/svg/PlusCircleIcon";

interface CustomSpecificSlot extends ISpecificSlotResponse {}

interface SpecificSlotProposalProps {
  meetingProposedBy: User;
  availabilitySlots: IAvailabilitySlot[];
  setAvailabilitySlots: React.Dispatch<SetStateAction<IAvailabilitySlot[]>>;
  groupOfProposals: TypeGroupOfProposals[];
  isHiringCompanyInvolved: boolean;
  isInviteSend: boolean;
  hasChanges: boolean;
  isEverySlotsDeclined: boolean;
  interviewDetails: IGetInterviewByIdResponse;
  setInterviewDetails: React.Dispatch<
    SetStateAction<IGetInterviewByIdResponse>
  >;
  availabilitySlotsRef: RefObject<IAvailabilitySlot[]>;
}

export function getSlotStatusForSpecificType({
  interviewDetails,
  statusOf,
}: {
  interviewDetails: IGetInterviewByIdResponse;
  statusOf?: EnumUserTypes;
}): slotStatus {
  if (
    (!interviewDetails?.isCandidateConfirm &&
      interviewDetails?.isCandidateConfirm === false &&
      statusOf === EnumUserTypes.CANDIDATE) ||
    (!interviewDetails?.isRecruiterAgencyConfirm &&
      interviewDetails?.isRecruiterAgencyConfirm === false &&
      statusOf === EnumUserTypes.RECRUITER)
  ) {
    return "Declined";
  } else if (
    (interviewDetails?.isCandidateConfirm &&
      interviewDetails?.isCandidateConfirm === true &&
      statusOf === EnumUserTypes.CANDIDATE) ||
    (interviewDetails?.isRecruiterAgencyConfirm &&
      interviewDetails?.isRecruiterAgencyConfirm === true &&
      statusOf === EnumUserTypes.RECRUITER)
  ) {
    return "Accepted";
  } else {
    return "Pending";
  }
}

const SpecificSlotProposal = (props: SpecificSlotProposalProps) => {
  const {
    meetingProposedBy,
    availabilitySlots,
    setAvailabilitySlots,
    groupOfProposals,
    isHiringCompanyInvolved,
    isInviteSend,
    hasChanges,
    isEverySlotsDeclined,
    availabilitySlotsRef,
    interviewDetails,
    setInterviewDetails,
  } = props;
  const specificSlotRef = useRef<CustomSpecificSlot>(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user: any = useSelector((state: RootState) => state.auth);

  const [searchParams, setSearchParams] = useSearchParams();
  const interviewId = searchParams.get("id");

  // Local state
  const [isForwardingInterview, setIsForwardingInterview] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const handleForwardMeetingToCandidate = async () => {
    try {
      setIsForwardingInterview(true);
      const { success, data } = await forwardInterview(interviewId);
      if (success) {
        setInterviewDetails(data);
        enqueueSnackbar("Interview forwarded successfully.", {
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Error: forwarding meeting to candidate", error);
    } finally {
      setIsForwardingInterview(false);
    }
  };

  const handleCounterProposalPoll = () => {
    navigate(`/counter-proposal-poll?id=${interviewId}`);
  };

  const meetingResponder = useMemo(() => {
    const parties = MeetingParties?.filter((party) => party !== user?.userType);
    return parties.filter((party) => EnumUserTypes.HIRING_COMPANY !== party);
  }, [isHiringCompanyInvolved, meetingProposedBy]);

  const isCounterProposalEnabled = useMemo(() => {
    return (
      interviewDetails?.isRecruiterAgencyConfirm === false ||
      interviewDetails?.isCandidateConfirm === false
    );
  }, [interviewDetails]);

  // const isConfirmChoicesEnabled = useMemo(() => {
  //   if (user?.userType === EnumUserTypes.CANDIDATE) {
  //     return interviewDetails?.isCandidateConfirm !== tempChoice;
  //   } else if (user?.userType === EnumUserTypes.CANDIDATE) {
  //     return interviewDetails?.isRecruiterAgencyConfirm !== tempChoice;
  //   } else {
  //     return false;
  //   }
  // }, [interviewDetails]);

  const isMeetingForwardedToCandidate = useMemo(
    () => interviewDetails?.interviewStatus === "Pending Acceptance",
    [interviewDetails]
  );

  const handleAcceptOrDeclineMeeting = async (type: "accept" | "decline") => {
    try {
      if (type === "accept") {
        setIsAccepting(true);
      } else {
        setIsDeclining(true);
      }
      const { success, data } = await acceptOrDeclineInviteConfirmation({
        id: interviewId,
        isConfirmInterview: type === "accept",
      });
      if (success) {
        setInterviewDetails(data);
      }
    } catch (error) {
      console.error("Error: accepting or declining specific interview", error);
    } finally {
      setIsAccepting(false);
      setIsDeclining(false);
    }
  };

  const isAccepted = useMemo(
    () =>
      user?.userType === EnumUserTypes.CANDIDATE
        ? interviewDetails?.isCandidateConfirm
        : interviewDetails?.isRecruiterAgencyConfirm,
    [interviewDetails, user]
  );

  const isDeclined = useMemo(
    () =>
      user?.userType === EnumUserTypes.CANDIDATE
        ? interviewDetails?.isCandidateConfirm === false
        : interviewDetails?.isRecruiterAgencyConfirm === false,
    [interviewDetails, user]
  );

  useEffect(() => {
    if (interviewDetails?.specificSlot) {
      specificSlotRef.current = interviewDetails?.specificSlot;
    }
  }, [interviewDetails?.specificSlot]);

  console.log("availabilitySlots", availabilitySlots);

  const isAcceptedTemp = false;
  const isDeclinedTemp = false;

  return (
    <div className="flex flex-col gap-3 mt-8">
      <div className="flex items-center">
        {user?.userType === EnumUserTypes?.RECRUITER &&
        !isMeetingForwardedToCandidate ? (
          <button
            onClick={handleForwardMeetingToCandidate}
            className="flex items-center gap-2 bg-primary rounded-md px-5 py-2 w-max text-xs text-white font-semibold hover:bg-black"
          >
            Forward invitation to candidate
            {isForwardingInterview ? <LoadingAnimation /> : null}
          </button>
        ) : null}
      </div>
      <div className="flex justify-between border-y border-dashed py-4 gap-8">
        {/* Column 1 */}
        <div className="flex flex-col flex-1 gap-5">
          <span className="font-semibold text-xs text-primary">
            {meetingProposedBy?.userType === userTypes.CANDIDATE
              ? "Candidate's"
              : meetingProposedBy?.userType === userTypes.RECRUITER
              ? "Agent's"
              : "Hiring Company's"}{" "}
            proposed times
          </span>
          <div className="flex flex-col gap-5">
            <div
              className={`grid grid-cols-2 items-center ${
                isInviteSend
                  ? "grid-cols-[1fr_0.7fr_0.7fr]"
                  : "grid-cols-[1fr_1fr]"
              }`}
            >
              <span className="text-xs font-medium text-light-black">
                {dayjs(interviewDetails?.specificSlot?.startTime).format(
                  "dddd MMM Do"
                )}
              </span>
              <span className="text-xs font-medium text-light-black">
                {dayjs(interviewDetails?.specificSlot?.startTime).format(
                  "HH:mm"
                )}
                &nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
                {dayjs(interviewDetails?.specificSlot?.endTime).format(
                  "HH:mm"
                )}
              </span>
            </div>
          </div>
        </div>
        {/* Column 2 */}
        {isHiringCompanyInvolved ? (
          <div className="flex flex-col flex-0.5 gap-5 text-center">
            <span className="font-semibold text-xs text-primary">
              {meetingResponder[0] === EnumUserTypes.CANDIDATE
                ? "Candidate's"
                : "Agent's"}{" "}
              response
            </span>
            <div className="flex flex-col gap-5">
              <span className="text-xs capitalize text-primary">
                {getSlotStatusForSpecificType({
                  interviewDetails,
                  statusOf: meetingResponder[0],
                })}
              </span>
            </div>
          </div>
        ) : null}
        {/* Column 3 */}
        <div
          className={`flex flex-col items-center gap-3.5 flex-[0.8] ${
            isCounterProposalEnabled && user?.userType !== EnumUserTypes.HIRING_COMPANY ? "opacity-50" : ""
          }`}
        >
          <div className="flex gap-3">
            <span className="font-semibold text-xs text-primary">
              {user?.userType !== EnumUserTypes.HIRING_COMPANY
                ? "Select your available slot"
                : meetingResponder[1] === EnumUserTypes.CANDIDATE
                ? "Candidate'sresponse"
                : "Agent's response"}
            </span>
          </div>
          {user?.userType === EnumUserTypes.HIRING_COMPANY ? (
            <div className="flex flex-col gap-5">
              <span className="text-xs capitalize text-primary">
                {getSlotStatusForSpecificType({
                  interviewDetails,
                  statusOf: meetingResponder[1],
                })}
              </span>
            </div>
          ) : (
            <div className={`flex flex-col items-center gap-1.5 w-[80%]`}>
              <div className="flex items-center w-full gap-2">
                <button
                  disabled={
                    isAccepting || isAccepted || isCounterProposalEnabled
                  }
                  onClick={() => handleAcceptOrDeclineMeeting("accept")}
                  className={`flex items-center justify-center ${
                    isAccepted
                      ? "bg-primary text-white"
                      : `bg-white border ${
                          !isCounterProposalEnabled
                            ? "hover:bg-gray-100"
                            : "opacity-50 cursor-not-allowed"
                        }`
                  } text-xs font-semibold rounded-md py-1.5 w-full gap-1`}
                >
                  {isAccepted ? "Accepted" : "Accept"}
                  {isAccepting ? (
                    <LoadingAnimation className="w-4 h-4" color="#2e2e2e" />
                  ) : null}
                </button>
                <button
                  disabled={
                    isDeclining || isDeclined || isCounterProposalEnabled
                  }
                  onClick={() => handleAcceptOrDeclineMeeting("decline")}
                  className={`flex items-center justify-center gap-1  ${
                    isDeclined
                      ? "bg-red-600 text-white"
                      : `bg-white border ${
                          !isCounterProposalEnabled
                            ? "hover:bg-gray-100"
                            : "opacity-50 cursor-not-allowed"
                        } text-primary`
                  } border  text-xs font-semibold rounded-md py-1.5 w-full `}
                >
                  {isDeclined ? "Declined" : "Decline"}
                  {isDeclining ? (
                    <LoadingAnimation className="w-4 h-4" color="#2e2e2e" />
                  ) : null}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Propose new time slot button */}
      {isCounterProposalEnabled ? (
        <div className="flex flex-col items-center mt-5 gap-3">
          <span className="text-xs font-medium text-light-black">
            If none of the above time slots match your availability, you may
            propose new one.
          </span>
          <button
            onClick={handleCounterProposalPoll}
            className="flex itesm-center justify-center text-white text-xs font-semibold bg-primary rounded-sm py-2 px-4 gap-1"
          >
            <PlusCircleIcon className={"w-4 h-4"} fill={"white"} />
            Propose new times.
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default SpecificSlotProposal;
